/**
 * Parsing for the RFC 9470 step-up challenge.
 *
 * **Deliberately dependency-free.** Both the axios interceptor
 * (`src/api/client.ts`) and the step-up flow (`src/auth/stepUp.ts`) need to read
 * the same header, and `stepUp` importing it from the client module would pull
 * the entire store chain — client → authStore → encryptionStore → the
 * platform-split `utils/crypto` — into anything that touches step-up. Its unit
 * tests mock `./completeOidcLogin` precisely so that chain never loads; a direct
 * import would walk straight past the mock and fail to resolve.
 *
 * The challenge extends RFC 6750's bearer challenge:
 *
 *     WWW-Authenticate: Bearer error="insufficient_user_authentication",
 *                       error_description="...", max_age="300"
 */

/** Read the `WWW-Authenticate` header, whatever case the transport used. */
function challengeHeader(error: unknown): string {
  const response = (error as { response?: { headers?: Record<string, unknown> } })?.response;
  const headers = response?.headers ?? {};
  // Header names are case-insensitive. axios lowercases them; a raw fetch and
  // some test doubles do not.
  return String(headers['www-authenticate'] ?? headers['WWW-Authenticate'] ?? '');
}

/**
 * Whether a failure is a step-up challenge rather than a dead session.
 *
 * Both are 401, which is what RFC 9470 specifies, so the header is the only
 * thing that tells them apart — and getting it wrong sends a step-up into the
 * token-refresh path, where it fails again and reads as a logout.
 */
export function isStepUpChallenge(error: unknown): boolean {
  const status = (error as { response?: { status?: number } })?.response?.status;
  if (status !== 401) return false;
  return challengeHeader(error).includes('insufficient_user_authentication');
}

/**
 * The `max_age` the server demanded, or null when it named no freshness bound.
 *
 * Null means the refusal was about the authentication *method* — a password
 * session where a passkey is required — so re-running the flow unchanged would
 * fail identically. RFC 9470 gives one error code for both cases; this is what
 * separates them.
 */
export function maxAgeFromChallenge(error: unknown): number | null {
  const match = /max_age="?(\d+)"?/.exec(challengeHeader(error));
  return match ? Number(match[1]) : null;
}
